import {
  callOnBeforeRun,
  type ChatEvent,
  ChatUiEventTypes,
  sendChatEvent,
  sendChatUiEvent,
} from '@trimble-agentic-external-npm-local/agentic-platform-sdk-iframe-typescript';
import {
  NarrowChat,
  type RunContextCallback,
} from '@trimble-agentic-internal-npm-local/agentic-chat-ui';
import { useCallback, useContext } from 'react';

import { useHostTools } from '../hooks/useHostTools';
import { useMcpAppOpenLink } from '../hooks/useMcpAppOpenLink';
import { EmbeddedConfigContext } from '../providers/embedded-config-provider';
import {
  resolveEmbeddedVariant,
  trackEmbeddedChatEvent,
} from '../utilities/embeddedAnalytics';
import { subscribeToParentSendMessage } from '../utilities/parentSendMessage';
import { mapDynamicToolsToRunContext } from '../utilities/toolUtils';

export const EmbeddedNarrowChat = () => {
  const context = useContext(EmbeddedConfigContext);
  const hostTools = useHostTools();
  const onMcpAppOpenLink = useMcpAppOpenLink();
  const showCloseButton =
    context?.chatConfig.uiConfig.showCloseButton !== false;
  const variant = resolveEmbeddedVariant(context?.chatConfig.uiConfig.variant);

  const handleChatEvent = useCallback(
    (event: ChatEvent) => {
      sendChatEvent(event.type, event.payload);
      trackEmbeddedChatEvent(variant, event, context?.chatConfig.agentId);
    },
    [context?.chatConfig.agentId, variant]
  );

  const handleOnBeforeRun = useCallback(
    async (
      agentId: string
    ): Promise<Awaited<ReturnType<RunContextCallback>>> => {
      const dynamicRunContext = await callOnBeforeRun(
        agentId,
        context?.chatConfig.onBeforeRunTimeout
      );

      return mapDynamicToolsToRunContext(dynamicRunContext);
    },
    [context?.chatConfig.onBeforeRunTimeout]
  );

  const handleClose = useCallback(() => {
    sendChatUiEvent({
      type: ChatUiEventTypes.OnClose,
      payload: undefined,
    });
  }, []);

  return (
    <div className='flex h-[100dvh] w-[100dvw] min-h-0 flex-col overflow-hidden'>
      <NarrowChat
        agentId={context?.chatConfig.agentId || ''}
        recommendedAgents={context?.chatConfig.uiConfig.productAgents}
        threadId={context?.chatConfig.threadId}
        hideModelSelection={
          context?.chatConfig.uiConfig.chatInput.hideModelSelection
        }
        chatInputButtons={context?.chatConfig.uiConfig.chatInput.buttons}
        onBeforeRunCall={handleOnBeforeRun}
        onChatEvent={handleChatEvent}
        hostTools={hostTools}
        onMcpAppOpenLink={onMcpAppOpenLink}
        mcpAppConfig={context?.chatConfig.mcpAppConfig}
        promptTiles={context?.chatConfig.uiConfig.promptTiles}
        showThinking={context?.chatConfig.uiConfig.showThinking}
        showAgentDropdown={context?.chatConfig.uiConfig.showAgentDropdown}
        localToolCancelledMessage={
          context?.chatConfig.localToolCancelledMessage
        }
        externalSendMessageSource={subscribeToParentSendMessage}
        onChatInputButtonClick={buttonId => {
          sendChatUiEvent({
            type: ChatUiEventTypes.OnChatInputButtonClick,
            payload: buttonId,
          });
        }}
        onClose={showCloseButton ? handleClose : undefined}
      />
    </div>
  );
};
